/**
 * ExternalRelease management page with list, search, and CRUD operations.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Space, message, Card, Modal, Drawer, Input, Form } from 'antd';
import { PlusOutlined, HistoryOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';

import { useExternalReleaseStore } from '@/store/externalReleaseStore';
import { useConfigStore } from '@/store/configStore';
import { useAuth } from '@/hooks/useAuth';
import { ExternalReleaseList } from '@/components/packages/ExternalReleaseList';
import { ExternalReleaseForm } from '@/components/packages/ExternalReleaseForm';
import { OperationLog } from '@/components/logs/OperationLog';
import { getExternalRelease, failExternalReleaseFileUpload, failExternalReleaseNoteUpload, failExternalReleaseTestReportUpload } from '@/api/externalRelease';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { useExternalReleaseStore as externalReleaseStoreRef } from '@/store/externalReleaseStore';
import type { ExternalRelease, ExternalReleaseDetail as ExternalReleaseDetailType, OperationLog as OperationLogType } from '@/types';

interface SearchFilters {
  vehicle_model?: string;
  created_by?: string;
}

const ExternalReleaseManagement = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, isAdmin, isExternal } = useAuth();
  const { releases, pagination, isLoading, fetchReleases, deleteRelease, setReleaseUploadStatus } = useExternalReleaseStore();
  const { projects } = useConfigStore();

  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [selectedRelease, setSelectedRelease] = useState<ExternalRelease | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isLogVisible, setIsLogVisible] = useState(false);
  const [logs, setLogs] = useState<OperationLogType[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRelease, setEditRelease] = useState<ExternalReleaseDetailType | null>(null);
  const [form] = Form.useForm<SearchFilters>();

  const currentProject = projects.find((p) => p.id === Number(projectId));

  // 项目列表由 App 统一加载（configStore 带缓存），此处不再重复拉取

  useEffect(() => {
    fetchReleases({
      project_id: projectId ? Number(projectId) : undefined,
      page: 1,
      page_size: 20,
    }).then(async () => {
      const STUCK_THRESHOLD_MS = 5 * 60 * 1000;
      const now = Date.now();
      // 页面初次加载：store 为空，无活跃上传，将所有 uploading/queued 文件标记为 failed
      const uploadingReleases = externalReleaseStoreRef.getState().releases.filter(r => r.upload_status === 'uploading');
      await Promise.allSettled(
        uploadingReleases.map(async (release) => {
          const detail = await getExternalRelease(release.id).catch(() => null);
          if (!detail) return;
          const progressStore = useUploadProgressStore.getState();
          const stuckFiles = (detail.files || []).filter(f => {
            if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
            const entry = progressStore.getProgress(`externalrelease-${release.id}-file-${f.id}`);
            if (entry && entry.status !== 'failed') return false;
            const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
            const staleSec = Math.round((now - updatedAt) / 1000);
            const isStuck = (now - updatedAt) > STUCK_THRESHOLD_MS;
            console.warn(
              '[failStuckFiles] er_id=%d file_id=%d file_name=%s status=%s: local_entry=%s updated_at=%s stale=%ds threshold=%ds → %s',
              release.id, f.id, f.file_name, f.upload_status, entry?.status ?? 'none', f.updated_at, staleSec, STUCK_THRESHOLD_MS / 1000, isStuck ? 'MARKING FAILED' : 'skip',
            );
            return isStuck;
          });
          const releaseUpdatedAt = detail.updated_at ? new Date(detail.updated_at).getTime() : Date.now();
          // If any file is still actively uploading/queued and recently heartbeated, RN/TR are
          // legitimately waiting in queue — don't mark them as stuck.
          const hasActiveFile = (detail.files || []).some(f => {
            if (f.upload_status !== 'uploading' && f.upload_status !== 'queued') return false;
            const updatedAt = f.updated_at ? new Date(f.updated_at).getTime() : Date.now();
            return (now - updatedAt) <= STUCK_THRESHOLD_MS;
          });
          const rnEntry = progressStore.getProgress(`externalrelease-${release.id}-doc-release_note`);
          const rnStuck = !!detail.momenta_release_note_name && !detail.momenta_release_note_url
            && (!rnEntry || rnEntry.status === 'failed')
            && (now - releaseUpdatedAt) > STUCK_THRESHOLD_MS
            && !hasActiveFile;
          const trPrefix = `externalrelease-${release.id}-doc-test_report`;
          const trActive = Object.entries(progressStore.progress).some(
            ([key, entry]) => (key === trPrefix || key.startsWith(`${trPrefix}-`))
              && !!entry && entry.status !== 'done' && entry.status !== 'failed',
          );
          const trEntry = progressStore.getProgress(trPrefix);
          const trStuck = !!detail.momenta_test_report_name && !detail.momenta_test_report_url
            && !trActive
            && (!trEntry || trEntry.status === 'failed')
            && (now - releaseUpdatedAt) > STUCK_THRESHOLD_MS
            && !hasActiveFile;
          if (stuckFiles.length > 0 || rnStuck || trStuck) {
            console.warn('[failStuckFiles] er_id=%d: marking stuck items — files=%o rnStuck=%s trStuck=%s', release.id, stuckFiles.map(f => f.id), rnStuck, trStuck);
          }
          await Promise.allSettled([
            ...stuckFiles.map(f => failExternalReleaseFileUpload(release.id, f.id)),
            ...(rnStuck ? [failExternalReleaseNoteUpload(release.id)] : []),
            ...(trStuck ? [failExternalReleaseTestReportUpload(release.id)] : []),
          ]);
        })
      );
    });
  }, [projectId, fetchReleases]);

  // Poll every 5s when any release is uploading
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasUploading = releases.some(r => r.upload_status === 'uploading');
    if (hasUploading && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchReleases({
          project_id: projectId ? Number(projectId) : undefined,
          page: 1,
          page_size: 20,
        });
      }, 5000);
    } else if (!hasUploading && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [releases, projectId, fetchReleases]);

  const handleSearch = useCallback(() => {
    const values = form.getFieldsValue();
    setSearchFilters(values);
    fetchReleases({
      project_id: projectId ? Number(projectId) : undefined,
      vehicle_model: values.vehicle_model || undefined,
      created_by: values.created_by || undefined,
      page: 1,
      page_size: 20,
    });
  }, [projectId, fetchReleases, form]);

  const handleReset = useCallback(() => {
    form.resetFields();
    setSearchFilters({});
    fetchReleases({
      project_id: projectId ? Number(projectId) : undefined,
      page: 1,
      page_size: 20,
    });
  }, [projectId, fetchReleases, form]);

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      fetchReleases({
        project_id: projectId ? Number(projectId) : undefined,
        ...searchFilters,
        page,
        page_size: pageSize,
      });
    },
    [projectId, searchFilters, fetchReleases]
  );

  const handleCreate = () => {
    setEditRelease(null);
    setIsEditMode(false);
    setIsFormVisible(true);
  };

  const handleEdit = async (release: ExternalRelease) => {
    try {
      const full = await getExternalRelease(release.id);
      setEditRelease(full);
      setIsEditMode(true);
      setIsFormVisible(true);
    } catch {
      message.error('获取详情失败');
    }
  };

  const handleViewLogs = async (release: ExternalRelease) => {
    setSelectedRelease(release);
    setIsLogVisible(true);
    setLogsLoading(true);
    try {
      const { getExternalReleaseLogs } = await import('@/api/externalRelease');
      const response = await getExternalReleaseLogs(release.id);
      setLogs(response.results);
    } catch {
      message.error('获取操作日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDelete = async (release: ExternalRelease) => {
    try {
      await deleteRelease(release.id);
      message.success('删除成功');
    } catch {
      message.error('删除失败');
    }
  };

  const handleFormSuccess = async (releaseId?: number) => {
    if (releaseId) {
      setReleaseUploadStatus(releaseId, 'uploading');
    }
    setIsFormVisible(false);
    await fetchReleases({
      project_id: projectId ? Number(projectId) : undefined,
      page: 1,
      page_size: 20,
    });
    // fetch 完成后再次覆盖，防止 fetch 用 DB 旧值覆盖乐观更新
    if (releaseId) {
      setReleaseUploadStatus(releaseId, 'uploading');
    }
  };

  // 外部用户对该页面只读，不提供新建入口
  const canCreate = !!user && !isExternal;

  return (
    <div className="p-4">
      <Card
        title={currentProject ? `${currentProject.name} - Momenta释放产物包` : 'Momenta释放产物包管理'}
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新建
            </Button>
          )
        }
      >
        {/* Search bar */}
        <Form form={form} layout="inline" className="mb-4">
          <Form.Item name="vehicle_model">
            <Input
              placeholder="按车型搜索"
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item name="created_by">
            <Input
              placeholder="按创建人搜索"
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={isLoading}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <ExternalReleaseList
          releases={releases}
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.count,
          }}
          projectId={projectId ? Number(projectId) : undefined}
          onEdit={handleEdit}
          onDelete={isAdmin ? handleDelete : undefined}
          onViewLogs={handleViewLogs}
          onPageChange={handlePageChange}
        />
      </Card>

      {/* Form Drawer */}
      <Drawer
        title={isEditMode ? '编辑Momenta释放产物包' : '新建Momenta释放产物包'}
        placement="right"
        styles={{ wrapper: { width: 1200 } }}
        open={isFormVisible}
        onClose={() => setIsFormVisible(false)}
        destroyOnHidden
        mask={{ closable: false }}
      >
        <ExternalReleaseForm
          currentProject={currentProject}
          initialData={isEditMode ? editRelease : null}
          isEdit={isEditMode}
          erId={editRelease?.id}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsFormVisible(false)}
        />
      </Drawer>

      {/* Operation Log Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            操作日志 - {selectedRelease?.name}
          </Space>
        }
        open={isLogVisible}
        onCancel={() => setIsLogVisible(false)}
        footer={null}
        width={900}
        destroyOnHidden
      >
        <OperationLog logs={logs} loading={logsLoading} />
      </Modal>
    </div>
  );
};

export default ExternalReleaseManagement;
